/**
 * FADING (craps) — non-shooter players cover the shooter's stack.
 * Mirror of cee-lo's FadingState; shooter-flavored copy.
 *
 * Mechanics identical: wallet opens for faders, stays closed for the
 * shooter who watches. Transitions out via server-driven
 * `bet_zone_filled` (stack fully covered) or `phase_timer_expired`.
 *
 * Craps wires `bankerId = shooterId` server-side, so `update.bankerId`
 * is the right field to use here too — keeping symmetry with the
 * cee-lo FadingState makes the migration one-to-one.
 */

import type { GameUpdate } from '@sdl/shared';
import { CrapsState } from '@sdl/shared';
import { State } from '../State.js';

export class CrapsFadingState extends State {
  async onEnter(update: GameUpdate): Promise<void> {
    const game = this.game;
    const shooterId = update.currentPlayerId ?? update.bankerId;
    const localId = game.getLocalPlayerId();
    const isLocalShooter = !!localId && shooterId === localId;

    game.playerProfilesOverlay?.show();
    game.playerProfilesOverlay?.setMode('betting');
    game.bettingHUD3D?.setLockInVisible(false);
    game.countdownOverlay?.stop();

    // Wallet open for the FADERS (everyone except the shooter).
    await game.enterBettingMode({
      open: !isLocalShooter,
      animated: update.animated,
      previousState: update.previousState,
    });

    if (isLocalShooter) {
      game.instructionBanner?.hide();
    } else {
      game.instructionBanner?.show(
        {
          title: "Shooter's Stack",
          text: 'Fade the shooter. Drag bills onto the stack',
        },
        update.animated,
      );
    }

    this._updateProfiles(update);
    await this._checkPlayers(update);
  }

  async onUpdate(update: GameUpdate): Promise<void> {
    this._updateProfiles(update);
    await this._checkPlayers(update);
  }

  async onExit(update: GameUpdate): Promise<void> {
    // Wipe the BETTING activity pill — the next state may or may not
    // be another betting phase. SIDEBET_BETTING reuses the betting
    // camera + wallet on its own entry, no other teardown needed.
    this._clearBettingActivity();

    // Heads-up exception: with 2 players the server skips SIDEBET_BETTING
    // (afterFading), so the pot-completing fade and state=COME_OUT ride
    // the SAME update. Bookkeeping has already started the fader's final
    // bill drop + the locked stack's consolidation (gated on the drop's
    // onAllSettled + a 350ms read beat). Hold the transition until that
    // finishes — otherwise ComeOutState.onEnter's exitBettingMode closes
    // the wallet, pans the camera, and wipes the bills mid-drop, and the
    // shooter never sees their stack get faded. The wait is settle-
    // guaranteed (bounded timeout inside), well under the TaskPool
    // watchdog. Destination SIDEBET_BETTING keeps the betting camera, so
    // the drop plays in frame there and no hold is needed. Skip when the
    // update isn't animated (state:full / resume snaps, nothing to watch).
    if (update.gameState === CrapsState.COME_OUT && update.animated) {
      await this.game.bettingController?.waitForLockedZoneTeardown();
    }
  }
}
